import { bindMenuDismissal } from "./actions.ts";
import { nextTaskIndex, type ListNavigationKey } from "../features/tasks/list-navigation.ts";
import { bindContextualHelp } from "../ui/contextual-help.ts";

export interface GlobalEventCapabilities {
  bindMobileGestures(): void;
  bindViewportResilience(): void;
  sync(): void | Promise<void>;
  markOffline(): void;
  refreshAccess(): void | Promise<void>;
  updateBackToTopVisibility(): void;
  quickNotesOpen(): boolean;
  flushQuickNote(): void | Promise<unknown>;
  composerSignature(form: HTMLFormElement): string;
  sidebarMenuOpen(): boolean;
  closeSidebarMenu(restoreFocus?: boolean): void;
  openQuickNotes(createNew?: boolean): void | Promise<void>;
  openCommandPalette(): void | Promise<void>;
  canCreateTask(): boolean;
  openNewTask(): void | Promise<void>;
  openTask(id: string): void | Promise<void>;
  motionBehavior(): ScrollBehavior;
  closeMobileSidebar(): void;
  closeQuickNotes(): void | Promise<void>;
  taskPanelOpen(): boolean;
  closeTaskComposer(): void | Promise<unknown>;
}

const listNavigationKeys = new Set<ListNavigationKey>(["j", "k", "arrowdown", "arrowup", "home", "end"]);

export function isListNavigationKey(key: string): key is ListNavigationKey {
  return listNavigationKeys.has(key as ListNavigationKey);
}

export function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement
    || target instanceof HTMLElement && target.isContentEditable;
}

export function bindGlobalEvents(capabilities: GlobalEventCapabilities): void {
  const scrollbarHideTimers = new WeakMap<Element, number>();
  bindMenuDismissal(document);
  bindContextualHelp(document);
  capabilities.bindMobileGestures();
  capabilities.bindViewportResilience();

  window.addEventListener("online", () => void capabilities.sync());
  window.addEventListener("offline", capabilities.markOffline);
  window.addEventListener("focus", () => void capabilities.refreshAccess());
  window.addEventListener("scroll", capabilities.updateBackToTopVisibility, { passive: true });
  document.addEventListener("scroll", event => showActiveScrollbar(event, scrollbarHideTimers), { capture: true, passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && capabilities.quickNotesOpen()) void capabilities.flushQuickNote();
  });
  window.addEventListener("pagehide", () => {
    if (capabilities.quickNotesOpen()) void capabilities.flushQuickNote();
  });
  window.addEventListener("beforeunload", event => {
    const form = document.querySelector<HTMLFormElement>("#detail-form");
    if (form && form.dataset.initialState !== capabilities.composerSignature(form)) event.preventDefault();
  });
  navigator.serviceWorker?.addEventListener("message", event => {
    if (event.data === "sync") void capabilities.sync();
  });
  document.addEventListener("click", event => {
    if (!capabilities.sidebarMenuOpen() || (event.target as HTMLElement).closest(".sidebar-menu-shell")) return;
    capabilities.closeSidebarMenu();
  });
  document.addEventListener("focusin", event => {
    if (!capabilities.sidebarMenuOpen() || (event.target as HTMLElement).closest(".sidebar-menu-shell")) return;
    capabilities.closeSidebarMenu();
  });
  document.addEventListener("keydown", event => handleGlobalKeydown(event, capabilities));
}

function showActiveScrollbar(event: Event, timers: WeakMap<Element, number>): void {
  const target = event.target instanceof Element ? event.target : document.documentElement;
  target.classList.add("is-scrolling");
  const previous = timers.get(target);
  if (previous) window.clearTimeout(previous);
  timers.set(target, window.setTimeout(() => {
    target.classList.remove("is-scrolling");
    timers.delete(target);
  }, 700));
}

function handleGlobalKeydown(event: KeyboardEvent, capabilities: GlobalEventCapabilities): void {
  if (event.defaultPrevented) return;
  // Native modal cancellation/focus containment takes precedence over the
  // underlying drawer or task panel. Menu handlers already consume their Escape.
  if (document.querySelector("dialog[open]")) return;
  if (event.key === "Tab" && document.body.classList.contains("nav-open")) {
    const sidebar = document.querySelector<HTMLElement>("#sidebar");
    const controls = sidebar ? Array.from(sidebar.querySelectorAll<HTMLElement>("button:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])")).filter(item => item.getClientRects().length) : [];
    if (controls.length) {
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!sidebar?.contains(document.activeElement) || event.shiftKey && document.activeElement === first) { event.preventDefault(); (event.shiftKey ? last : first).focus(); return; }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); return; }
    }
  }
  const typing = isTypingTarget(event.target);
  const key = event.key.toLowerCase();
  if (!typing && event.key === "\\") {
    const column = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>(".kanban-column");
    const toggle = column?.querySelector<HTMLButtonElement>("[data-action=toggle-board-column]");
    if (toggle) { event.preventDefault(); toggle.click(); return; }
  }
  if (!typing && (event.metaKey || event.ctrlKey) && !event.shiftKey && key === "z") {
    const actions = document.querySelectorAll<HTMLButtonElement>("[data-task-undo]");
    const action = actions.item(actions.length - 1);
    if (action) { event.preventDefault(); action.click(); return; }
  }
  if (!typing && key === "q" && !event.metaKey && !event.ctrlKey && !event.altKey) { event.preventDefault(); void capabilities.openQuickNotes(); return; }
  if (!typing && event.altKey && !event.metaKey && !event.ctrlKey && key === "n") { event.preventDefault(); void capabilities.openQuickNotes(true); return; }
  if ((event.metaKey || event.ctrlKey) && key === "k") { event.preventDefault(); void capabilities.openCommandPalette(); return; }
  if (!typing && event.key === "/") { event.preventDefault(); document.querySelector<HTMLInputElement>("#search")?.focus(); return; }
  if (!typing && key === "n" && capabilities.canCreateTask()) { event.preventDefault(); void capabilities.openNewTask(); return; }
  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
    const form = document.querySelector<HTMLFormElement>("#detail-form");
    if (form) { event.preventDefault(); form.requestSubmit(); return; }
  }
  if (!typing && isListNavigationKey(key)) {
    event.preventDefault();
    const rows = Array.from(document.querySelectorAll<HTMLElement>("[data-todo]"));
    if (!rows.length) return;
    const current = rows.findIndex(row => row.contains(document.activeElement));
    const next = nextTaskIndex(current, rows.length, key);
    rows.forEach(row => { row.tabIndex = -1; });
    rows[next].tabIndex = 0;
    rows[next].focus();
    rows[next].scrollIntoView({ behavior: capabilities.motionBehavior(), block: "nearest" });
    return;
  }
  if (!typing && event.key === "Enter" && !(event.target as Element | null)?.closest("button, a, summary, [role='button'], [role='menuitem']")) {
    const row = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>("[data-todo]");
    if (row?.dataset.todo) void capabilities.openTask(row.dataset.todo);
  }
  if (event.key === "Escape" && capabilities.sidebarMenuOpen()) { capabilities.closeSidebarMenu(true); return; }
  if (event.key === "Escape" && document.body.classList.contains("nav-open")) { event.preventDefault(); capabilities.closeMobileSidebar(); return; }
  if (event.key === "Escape" && capabilities.quickNotesOpen() && !document.querySelector("dialog[open]")) { event.preventDefault(); void capabilities.closeQuickNotes(); return; }
  if (event.key === "Escape" && capabilities.taskPanelOpen()) { event.preventDefault(); void capabilities.closeTaskComposer(); }
}
